/* eslint-disable @typescript-eslint/no-unused-vars */
import { UserInfo } from "../redux/user/userSlice";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SchoolIcon from "@mui/icons-material/School";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import DoorSlidingIcon from "@mui/icons-material/DoorSliding";
import VisibilityIcon from "@mui/icons-material/Visibility";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import ForumIcon from "@mui/icons-material/Forum";
import RuleIcon from "@mui/icons-material/Rule";

export enum ObjectTarget {
    UNKNOWN = -1,
    SYSTEM = 0,
    SCHOOL = 1,
    CLASSROOM = 2,
    STUDENT = 3,
    USER = 4,
    DPTO = 8,
    ROUTINE = 9,
    ROUTINE_ITEM = 10,
    ROUTINE_ITEM_OPTION = 11,
    SUBJECT = 12,
}

export function getObjectTargetName(id?: ObjectTarget): string {
    switch (id) {
        case ObjectTarget.SYSTEM:
            return "Sistema";
        case ObjectTarget.SCHOOL:
            return "Escola";
        case ObjectTarget.CLASSROOM:
            return "Sala de Aula";
        case ObjectTarget.STUDENT:
            return "Aluno";
        case ObjectTarget.USER:
            return "Usuário";
        case ObjectTarget.DPTO:
            return "Departamento";
        case ObjectTarget.ROUTINE:
            return "Rotina";
        case ObjectTarget.ROUTINE_ITEM:
            return "Item de Rotina";
        case ObjectTarget.ROUTINE_ITEM_OPTION:
            return "Opção de Item de Rotina";
        case ObjectTarget.SUBJECT:
            return "Disciplina";
        default:
            return "Desconhecido";
    }
}

export enum PermissionId {
    SYSTEM_ADMIN = "1",

    SCHOOL_ADMIN = "1000",
    SCHOOL_TEACHER = "1001",
    SCHOOL_MESSAGE = "1002",
    SCHOOL_IMG_REVIEWER = "1010",
    SCHOOL_GATE = "1020",
    SCHOOL_VIEWER = "1099",

    CLASSROOM_ADMIN = "2000",
    CLASSROOM_TEACHER = "2001",
    CLASSROOM_MESSAGE = "2002",
    CLASSROOM_IMG_REVIEWER = "2010",
    CLASSROOM_VIWER = "2099",

    STUDENT_ADMIN = "3000",
    STUDENT_PARENT = "3001",
    STUDENT_VIEWER = "3099",

    DPTO_ADMIN = "8000",
    DPTO_MESSAGE = "8002",
    // DPTO_VIEWER = "8099",
}

export interface Permission {
    id: string;
    name: string;
    icon: React.ReactNode;
    target: ObjectTarget;
    description: string;
}

export const PERMISSIONS: Record<PermissionId, Permission> = {
    [PermissionId.SYSTEM_ADMIN]: { 
        id: "1", 
        name: "Administrador", 
        icon: <AdminPanelSettingsIcon />, 
        target: ObjectTarget.SYSTEM,
        description: "Permite ao usuário acessar todas as funcionalidades do sistema.",
    },

    [PermissionId.SCHOOL_ADMIN]: { 
        id: "1000", 
        name: "Administrador", 
        icon: <AdminPanelSettingsIcon />, 
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário gerenciar a escola, suas salas de aula e alunos, assim como revisar/apagar as imagens enviadas para a escola ou salas de aula. Não permite enviar mensagens.",
    },
    [PermissionId.SCHOOL_TEACHER]: { 
        id: "1001", 
        name: "Professor", 
        icon: <SchoolIcon />, 
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário gerenciar a escola, enviar mensagens e auxiliar nas atividades gerais relacionadas à escola. Não permite enviar mensagens, e não permite revisar imagens.",
    },
    [PermissionId.SCHOOL_MESSAGE]: { 
        id: "1002", 
        name: "Mensagens", 
        icon: <ForumIcon />, 
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário enviar mensagens para a escola.",
    },
    [PermissionId.SCHOOL_IMG_REVIEWER]: { 
        id: "1010", 
        name: "Revisor de Imagens",
        icon: <RuleIcon />,
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário revisar e aprovar imagens enviadas para a escola e suas salas de aula.",
    },
    [PermissionId.SCHOOL_GATE]: { 
        id: "1020", 
        name: "Saída de Alunos",
        icon: <DoorSlidingIcon />,
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário acessar a funcionalidade de saída de alunos.",
    },
    [PermissionId.SCHOOL_VIEWER]: { 
        id: "1099", 
        name: "Visualizador", 
        icon: <VisibilityIcon />, 
        target: ObjectTarget.SCHOOL,
        description: "Permite ao usuário visualizar informações da escola e suas fotos.",
    },

    [PermissionId.CLASSROOM_ADMIN]: { 
        id: "2000", 
        name: "Administrador", 
        icon: <AdminPanelSettingsIcon />, 
        target: ObjectTarget.CLASSROOM,
        description: "Permite ao usuário gerenciar a sala de aula, seus alunos e professores, assim como revisar/apagar imagens. Não permite enviar mensagens.",
    },
    [PermissionId.CLASSROOM_TEACHER]: { 
        id: "2001", 
        name: "Professor", 
        icon: <SchoolIcon />, 
        target: ObjectTarget.CLASSROOM,
        description: "Permite ao usuário gerenciar a sala de aula, enviar mensagens e auxiliar nas atividades gerais relacionadas à sala de aula, como a gestão da rotina. Não permite revisar imagens.",
    },
    [PermissionId.CLASSROOM_MESSAGE]: { 
        id: "2002", 
        name: "Mensagens", 
        icon: <ForumIcon />, 
        target: ObjectTarget.CLASSROOM,
        description: "Permite ao usuário enviar mensagens para a sala de aula.",
    },
    [PermissionId.CLASSROOM_IMG_REVIEWER]: {
        id: "2010",
        name: "Revisor de Imagens",
        icon: <RuleIcon />,
        target: ObjectTarget.CLASSROOM,
        description: "Permite ao usuário revisar imagens enviadas para a sala de aula.",
    },
    [PermissionId.CLASSROOM_VIWER]: { 
        id: "2099", 
        name: "Visualizador", 
        icon: <VisibilityIcon />, 
        target: ObjectTarget.CLASSROOM,
        description: "Permite ao usuário visualizar informações da sala de aula e suas fotos.",
    },

    [PermissionId.STUDENT_ADMIN]: { 
        id: "3000", 
        name: "Responsável", 
        icon: <AdminPanelSettingsIcon />, 
        target: ObjectTarget.STUDENT,
        description: "Permite ao usuário mudar dados do aluno, convidar famliares, ver e enviar mensagens.",
    },
    [PermissionId.STUDENT_PARENT]: { 
        id: "3001", 
        name: "Familiar", 
        icon: <EscalatorWarningIcon />, 
        target: ObjectTarget.STUDENT,
        description: "Permite ao usuário visualizar informações, ver e enviar mensagens.",
    },
    [PermissionId.STUDENT_VIEWER]: { 
        id: "3099", 
        name: "Visualizador", 
        icon: <VisibilityIcon />, 
        target: ObjectTarget.STUDENT,
        description: "Permite ao usuário visualizar informações do aluno, suas fotos, ver e enviar mensagens.",
    },

    [PermissionId.DPTO_ADMIN]: { 
        id: "8000", 
        name: "Administrador", 
        icon: <AdminPanelSettingsIcon />, 
        target: ObjectTarget.DPTO,
        description: "Permite ao usuário gerenciar o departamento. Não permite enviar mensagens.",
    },
    [PermissionId.DPTO_MESSAGE]: { 
        id: "8002", 
        name: "Mensagens", 
        icon: <ForumIcon />, 
        target: ObjectTarget.DPTO,
        description: "Permite ao usuário enviar mensagens para o departamento.",
    },
    // [PermissionId.DPTO_VIEWER]: { id: "8099", name: "Visualizador", icon: <VisibilityIcon />, target: ObjectTarget.DPTO },
};

export const ALL_CLASSROOM_PERMISSIONS: PermissionId[] = Object.entries(PERMISSIONS)
    .filter(([key, value]) => value.target === ObjectTarget.CLASSROOM)
    .map(([key, value]) => key as PermissionId);

export const CLASSROOM_PERMISSIONS: PermissionId[] = ALL_CLASSROOM_PERMISSIONS
    .filter((value) => (parseInt(value) % 1000) < 99);

export const ALL_SCHOOL_PERMISSIONS: PermissionId[] = Object.entries(PERMISSIONS)
    .filter(([key, value]) => value.target === ObjectTarget.SCHOOL)
    .map(([key, value]) => key as PermissionId);

export const SCHOOL_PERMISSIONS: PermissionId[] = ALL_SCHOOL_PERMISSIONS
    .filter((value) => (parseInt(value) % 1000) < 99);

export const ALL_STUDENT_PERMISSIONS: PermissionId[] = Object.entries(PERMISSIONS)
    .filter(([key, value]) => value.target === ObjectTarget.STUDENT)
    .map(([key, value]) => key as PermissionId);

export const STUDENT_PERMISSIONS: PermissionId[] = ALL_STUDENT_PERMISSIONS;

export const ALL_DPTO_PERMISSIONS: PermissionId[] = Object.entries(PERMISSIONS)
    .filter(([key, value]) => value.target === ObjectTarget.DPTO)
    .map(([key, value]) => key as PermissionId);

export const DPTO_PERMISSIONS: PermissionId[] = ALL_DPTO_PERMISSIONS
    .filter((value) => (parseInt(value) % 1000) < 99);

export const PERMISSIONS_TO_ALLOW_CONVERSATION: PermissionId[] = [
    PermissionId.SCHOOL_MESSAGE,
    PermissionId.CLASSROOM_MESSAGE,
    PermissionId.CLASSROOM_TEACHER,
    PermissionId.STUDENT_ADMIN, 
    PermissionId.STUDENT_PARENT, 
    PermissionId.STUDENT_VIEWER,
    PermissionId.DPTO_MESSAGE,
];

export function getPermission(permission: PermissionId | string): Permission {
    if (!PERMISSIONS[permission as PermissionId]) {
        return { 
            id: "0", 
            name: "Desconhecido", 
            icon: <QuestionMarkIcon />, 
            target: ObjectTarget.SYSTEM,
            description: "Permissão desconhecida",
        };
    }
    return PERMISSIONS[permission as PermissionId];
}

export const SYSTEM_TARGET = "1";

export function anyPermission(user: UserInfo | undefined, target: string | undefined, permissions: PermissionId[]): boolean {
    if( checkIsAdmin(user) )
        return true;
    return anyDirectPermission(user, target, permissions);
}

export function hasPermission(user: UserInfo | undefined, permissionId: PermissionId, target?: string) {
    return checkIsAdmin(user) || hasDirectPermission(user, permissionId, target);
}

export function anyDirectPermission(user: UserInfo | undefined, target: string | undefined, permissions: PermissionId[]): boolean {
    for (const permissionId of permissions) {
        if (hasDirectPermission(user, permissionId, target)) {
            return true;
        }
    }
    return false;
}

export function hasDirectPermission(user: UserInfo | undefined, permissionId: PermissionId, target?: string) {
    if (!user)
        return false;
    if (!user.permissions)
        return false;

    if (user.permissions[permissionId]) {
        if (user.permissions[permissionId].indexOf(`${target}`) >= 0) {
            return true;
        }
    }

    return false;
}

export function checkIsAdmin(user: UserInfo | undefined) {
    return hasDirectPermission(user, PermissionId.SYSTEM_ADMIN, SYSTEM_TARGET);
}

